import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Tooltip,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { ReactComponent as PenIcon } from "../../../images/penIcon.svg";
import CommonAccordion from "../../custom-components/CommonAccordion";
import CommonDownload from "../../custom-components/CommonDownload";
import CommonCopy from "../../custom-components/CommonCopy";
import { generateTestConditions } from "../../../services/webServices";
import { useLocation, useNavigate } from "react-router-dom";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import { flattenArray, flattenObject } from "../../../utils/dataFlatteners";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import {
  TestCaseFlowDataContext,
  TestCaseLoaderStateContext,
} from "../../main-pages/TestcaseGenerationPage";
import MessagePopup from "../../popup-components/MessagePopup";

const UseCasesComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {setLoadingState} = useContext(LoadingModalContext);
  const { setTestCaseLoaderState } = useContext(TestCaseLoaderStateContext);
  const { functionalSpecDocument, useCase, testConditions, setTestConditions } =
    useContext(TestCaseFlowDataContext);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [error, setError] = useState(null);
  const editableFields = useCase || {};
  const [activeThumb, setActiveThumb] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const defaultUseCaseExpanded =
    Object.keys(useCase || {}).length > 0 &&
    Object.keys(testConditions || {}).length === 0;
  const useCaseData = {};
  Object.entries(editableFields).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      useCaseData[key] = flattenArray(value);
    } else if (!!value && typeof value === "object") {
      useCaseData[key] = flattenObject(value);
    } else {
      useCaseData[key] = value;
    }
  });

  const handleDownloadClick = () => {
    setDownloadOpen(true);
  };

  const handleCloseDownload = () => {
    setDownloadOpen(false);
  };

  const handleGenerateTestConditions = async () => {
    let fake;

    const startProgress = (setProgress) => {
      let progress = 0;
      let stop = false;
      let finalMessageShown = false;

      const interval = setInterval(() => {
        if (stop) return;

        progress += 1.7;
        if (progress >= 98 && !finalMessageShown) {
          finalMessageShown = true;
          setLoadingState((prev) => ({
            ...prev,
            message: "Almost done! Fetching your output",
          }));
        }

        if (progress >= 98) progress = 98;
        setProgress((prev) => ({
          ...prev,
          progress: Math.max(prev.progress, Math.floor(progress)),
        }));
      }, 200);

      return {
        stop: () => {
          stop = true;
          clearInterval(interval);
        },
        completeTo100: async () => {
          stop = true;
          clearInterval(interval);

          // Rush to 100%
          setProgress((prev) => ({
            ...prev,
            progress: 100,
          }));
          await new Promise((res) => setTimeout(res, 500));
        },
      };
    };

    const finishTo100AndClose = async () => {
      await fake.completeTo100();
      setLoadingState({ open: false, message: "", progress: null });
    };
    // Initial message
    setLoadingState({
      open: true,
      message: "Generating Test Conditions",
      progress: 0,
    });

    fake = startProgress(setLoadingState);
    try {
      setError(null);
      setTestCaseLoaderState("Test Conditions");
      setTestConditions([]);

      const testConditions = await generateTestConditions({
        useCase: useCase,
        functionalSpecDocument: functionalSpecDocument["Functional Requirements"],
      });

      if (!testConditions?.data || testConditions.data.length === 0) {
        setPopupMessage("No test conditions received from the server.");
        setPopupOpen(true);

        return;
      }

      await finishTo100AndClose();
      navigate("/dashboard/generate-testcases/output", {
        state: {
          ...location.state,
          testConditions: testConditions.data || [],
          testTechniques: {},
        },
        replace: true,
      });
    } catch (error) {
      console.error("Error generating test conditions:", error);
      const errorMsg =
        error?.response?.data?.Error ||
        "An error occurred while generating test conditions.";
      setPopupMessage(errorMsg);
      setPopupOpen(true);
      setError("Error generating test conditions: " + error.message);
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      setLoadingState({ open: false, message: "", progress: null });
    }
  };


  const handleThumbClick = (thumbType) => {
    setActiveThumb((prev) => (prev === thumbType ? null : thumbType));
  };

  const EditableField = ({ label, initialValue }) => {
    const [value, setValue] = useState(initialValue);
    const [tempValue, setTempValue] = useState(initialValue);
    const [isEditing, setIsEditing] = useState(false);

    const handleSave = () => {
      setValue(tempValue);
      const updatedUseCase = { ...useCaseData, [label]: tempValue };
      navigate("/dashboard/generate-testcases/output", {
        state: { ...location.state, useCase: updatedUseCase },
        replace: true,
      });
      setIsEditing(false);
    };

    const handleCancel = () => {
      setTempValue(value);
      setIsEditing(false);
    };

    return (
      <Box sx={{ marginBottom: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ color: "gray", fontSize: "14px" }}
        >
          {label}:
        </Typography>
        {isEditing ? (
          <Box display="flex" alignItems="flex-start">
            <TextField
              fullWidth
              multiline
              variant="outlined"
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              sx={{ marginRight: 1 }}
            />
            <Box display="flex" flexDirection="column">
              <Button
                onClick={handleSave}
                variant="text"
                color="primary"
                sx={{ marginBottom: 1, textTransform: "none" }}
              >
                Save
              </Button>
              <Button
                onClick={handleCancel}
                variant="text"
                sx={{ marginTop: -2, textTransform: "none" }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              position: "relative",
              "&:hover .editIcon": {
                visibility: "visible",
              },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                whiteSpace: "pre-wrap",
                overflowWrap: "break-word",
                fontSize: "14px",
                flexGrow: 1,
              }}
            >
              {value}
            </Typography>
            <IconButton
              onClick={() => setIsEditing(true)}
              sx={{
                position: "relative",
                right: 0,
                marginLeft: "8px",
              }}
              className="editIcon"
            >
              <PenIcon />
            </IconButton>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <CommonAccordion
        summary={"Generated Use Case"}
        details={
          <Box>
            {Object.entries(useCaseData).map(([label, value]) => (
              <EditableField key={label} label={label} initialValue={value} />
            ))}
            <Box sx={{ display: "flex", alignItems: "center", marginTop: 2 }}>
              {/* Thumbs Up Button with Tooltip */}
              <Tooltip title="Like" arrow>
                <Box
                  onClick={() => handleThumbClick("like")}
                  sx={{
                    cursor: "pointer",
                    marginRight: 2,
                    "&:hover": {
                      color: "#49A3FF",
                    },
                  }}
                >
                  {activeThumb === "like" ? (
                    <ThumbUpIcon sx={{ fontSize: "24px" }} />
                  ) : (
                    <ThumbUpOutlinedIcon sx={{ fontSize: "24px" }} />
                  )}
                </Box>
              </Tooltip>

              {/* Thumbs Down Button with Tooltip */}
              <Tooltip title="Dislike" arrow>
                <Box
                  onClick={() => handleThumbClick("dislike")}
                  sx={{
                    cursor: "pointer",
                    marginRight: 2,
                    "&:hover": {
                      color: "#FF4842",
                    },
                  }}
                >
                  {activeThumb === "dislike" ? (
                    <ThumbDownIcon sx={{ fontSize: "24px" }} />
                  ) : (
                    <ThumbDownOutlinedIcon sx={{ fontSize: "24px" }} />
                  )}
                </Box>
              </Tooltip>
              <CommonCopy contentToCopy={useCaseData} />
            </Box>
          </Box>
        }
        buttons={[
          {
            label: "Download",
            onClick: handleDownloadClick,
            variant: "text",
            startIcon: <FileDownloadOutlinedIcon />,
            sx: { fontSize: "14px", textTransform: "none" },
          },
          {
            label: "Generate Test Conditions",
            onClick: handleGenerateTestConditions,
            startIcon: <GenerateButton />,
            variant: "contained",
            sx: { float: "right", fontSize: "14px", textTransform: "none" },
          },
        ]}
        type={"output"}
        defaultExpanded={defaultUseCaseExpanded}
        fullScreenMode={true}
      />
      <CommonDownload
        open={downloadOpen}
        onClose={handleCloseDownload}
        serviceName="Use Cases Report"
        wordData={[
          { heading: "Use Case", type: "subParagraphs", data: useCaseData },
        ]}
        serviceResponse={[
          { heading: "Use Case", type: "subParagraphs", data: useCaseData },
        ]}
        documents={["docx", "pdf"]}
      />
      <MessagePopup
        modalOpen={popupOpen}
        okayHandler={() => setPopupOpen(false)}
        message={popupMessage} />

    </Box>
  );
};

export default UseCasesComponent;
